import { useRef, useState, useEffect } from "react";
import "./SliderWrapper.css";

const SliderWrapper = ({
	slides = [],
	children,
	onActiveIndexChange = () => {},
	initialIndex = 0,
}) => {
	const scrollRef = useRef(null); // Ref for slider-scroll
	const titleScrollRef = useRef(null); // Ref for slider-title
	const [activeIndex, setActiveIndex] = useState(initialIndex);
	const isScrollingRef = useRef(false);

	// Scroll to the given index
	const scrollToIndex = index => {
		if (!scrollRef.current || index < 0 || index >= slides.length) return;
		const container = scrollRef.current;
		const slide = container.children[index];

		isScrollingRef.current = true;

		container.scrollTo({
			left:
				slide.offsetLeft - container.offsetWidth / 2 + slide.offsetWidth / 2,
			behavior: "smooth",
		});

		setActiveIndex(index);
		onActiveIndexChange(index); // Notify parent

		setTimeout(() => {
			isScrollingRef.current = false;
		}, 800);
	};

	// Scroll to the initial index on first mount
	useEffect(() => {
		// Delay to allow components to render
		setTimeout(() => {
			scrollToIndex(initialIndex);
		}, 50);
	}, [initialIndex]);

	// Automatically center the active title
	useEffect(() => {
		if (!titleScrollRef.current) return;

		const container = titleScrollRef.current;
		const activeTitle = container.children[activeIndex];
		if (!activeTitle) return;

		container.scrollTo({
			left:
				activeTitle.offsetLeft -
				container.offsetWidth / 2 +
				activeTitle.offsetWidth / 2,
			behavior: "smooth",
		});
	}, [activeIndex]);

	// On swipe scroll
	const handleScroll = () => {
		if (isScrollingRef.current) return;

		const container = scrollRef.current;
		if (!container) return;

		const childrenArray = Array.from(container.children);
		const containerCenter = container.scrollLeft + container.offsetWidth / 2;

		const closestIndex = childrenArray.reduce(
			(closest, child, index) => {
				const childCenter = child.offsetLeft + child.offsetWidth / 2;
				const diff = Math.abs(childCenter - containerCenter);
				return diff < closest.diff ? { index, diff } : closest;
			},
			{ index: 0, diff: Infinity }
		).index;

		if (closestIndex !== activeIndex) {
			setActiveIndex(closestIndex);
			onActiveIndexChange(closestIndex); // Callback to parent
		}
	};

	return (
		<div className="slider-wrapper-container">
			<div className="slider-title" ref={titleScrollRef}>
				{slides.map((slide, idx) => (
					<span
						key={idx}
						className={`title-item ${idx === activeIndex ? "active" : ""}`}
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							scrollToIndex(idx);
						}}
					>
						{slide.label}
					</span>
				))}
			</div>

			<div
				className="slider-scroll"
				ref={scrollRef}
				onScroll={handleScroll}
				tabIndex={0}
			>
				{slides.map((slide, index) => (
					<div
						className={`slide-item ${index === activeIndex ? "active" : ""}`}
						key={index}
					>
						{slide.rows.map((row, i) =>
							children(
								row,
								i,
								slide.stats?.totalExpenses ?? 0,
								slide.stats?.totalIncome ?? 0,
								slide
							)
						)}
					</div>
				))}
			</div>

			<div className="slider-dots">
				{slides.map((_, idx) => (
					<span
						key={idx}
						className={`dot ${idx === activeIndex ? "active" : ""}`}
						onClick={e => {
							e.preventDefault();
							e.stopPropagation();
							scrollToIndex(idx);
						}}
					/>
				))}
			</div>
		</div>
	);
};

export default SliderWrapper;
